import i18n from '../../../i18n'
import { importActionApi } from '../../../api/actionImportApi'
import { extractErrorMessage } from '../../../utils/errorHandler'
import { ImportRow, toImportPayload } from './importRowModel'

export type ImportOutcome =
  | { rowId: string; ok: true }
  | { rowId: string; ok: false; message: string }

const MAX_PARALLEL = 4

function readableError(error: unknown): string {
  const message = extractErrorMessage(error)
  if (message.includes('Weight and TargetScore are required')) {
    return i18n.t('alerts.actionAdd.weightTargetRequired')
  }
  const translated = i18n.t(`errors.${message}`, { defaultValue: '' })
  return translated || message
}

/**
 * Sends one POST /action-import/import per row.
 *
 * Rows that target the same project run one after another: the backend checks
 * the remaining project budget against already-saved actions, so parallel
 * requests could each pass the check and overspend the budget together.
 */
export async function runImport(
  rows: ImportRow[],
  onOutcome: (outcome: ImportOutcome) => void,
  shouldStop: () => boolean
): Promise<void> {
  const lanes = new Map<string, ImportRow[]>()
  rows.forEach((row) => {
    const laneKey = row.ProjetId ? `p:${row.ProjetId}` : `r:${row.id}`
    const lane = lanes.get(laneKey)
    if (lane) lane.push(row)
    else lanes.set(laneKey, [row])
  })

  const queue = Array.from(lanes.values())

  const worker = async () => {
    while (queue.length && !shouldStop()) {
      const lane = queue.shift()
      if (!lane) return
      for (const row of lane) {
        if (shouldStop()) return
        try {
          await importActionApi(toImportPayload(row))
          onOutcome({ rowId: row.id, ok: true })
        } catch (error) {
          onOutcome({ rowId: row.id, ok: false, message: readableError(error) })
        }
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(MAX_PARALLEL, queue.length) }, worker)
  )
}
